/**
 * ParameterLogsPage — /logs/parameters — chart + history for manual test readings.
 *
 * Reads from ManualReadingStore (IndexedDB via idb). Provides parameter chips,
 * brand filter, date-range picker, CSV export, Print, a Health Report, an
 * Apex/Trident overlay chart, drift detection (test kit vs Apex), per-parameter
 * trend summaries (7d/30d deltas), and overdue-test reminders. The history
 * table collapses to cards below `md` for phones.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ManualReading } from '@reefapp/shared';
import {
  ALL_TEST_KITS,
  computeDrift,
  detectTrend,
  staleParameters,
  summarizePeriods,
} from '@reefapp/shared';

import {
  remove,
  subscribe,
  migrateFromLogPro,
} from '../manual-readings/ManualReadingStore';
import { ensureParameterLogSync } from '../manual-readings/readingSync';
import { toCsv } from '../manual-readings/exportCsv';
import { HealthReportView } from '../manual-readings/HealthReportView';
import { ParameterTrendChart } from '../manual-readings/ParameterTrendChart';
import { useApexOverlay } from '../manual-readings/useApexOverlay';
import type { ProbeId } from '../lib/probe-thresholds';

type DateRange = '7d' | '30d' | '90d' | 'all';

/** |manual − apex| beyond this ⇒ suggest recalibration. */
const DRIFT_TOLERANCE: Record<ProbeId, number> = {
  alk: 0.5, // dKH
  ca: 20, // ppm
  mg: 50, // ppm
  pH: 0.1,
  tmp: 0.5, // °F
  orp: 25, // mV
};

function rangeToSince(range: DateRange): string | undefined {
  if (range === 'all') return undefined;
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function downloadBlob(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ParameterLogsPage() {
  const [readings, setReadings] = useState<readonly ManualReading[]>([]);
  const [selectedParameter, setSelectedParameter] = useState<string>('All');
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [brandFilter, setBrandFilter] = useState('All');
  const [showReport, setShowReport] = useState(false);

  // Init: subscribe to store + run migration + start Pi mirroring
  useEffect(() => {
    ensureParameterLogSync();
    void migrateFromLogPro();
    const unsub = subscribe((snapshot) => {
      setReadings(snapshot);
    });
    return unsub;
  }, []);

  // Derive available parameters
  const parameters = useMemo(() => {
    const set = new Set<string>();
    for (const r of readings) {
      set.add(r.parameter);
    }
    return ['All', ...[...set].sort()];
  }, [readings]);

  const brands = useMemo(() => {
    const set = new Set<string>();
    for (const r of readings) {
      set.add(r.brand);
    }
    return ['All', ...[...set].sort()];
  }, [readings]);

  // Filter readings
  const filtered = useMemo(() => {
    const since = rangeToSince(dateRange);
    return readings.filter((r) => {
      if (selectedParameter !== 'All' && r.parameter !== selectedParameter) return false;
      if (brandFilter !== 'All' && r.brand !== brandFilter) return false;
      if (since && r.timestamp < since) return false;
      return true;
    });
  }, [readings, selectedParameter, brandFilter, dateRange]);

  // Latest reading for the selected parameter (for action plan card)
  const latestReading = useMemo(() => {
    if (selectedParameter === 'All') return null;
    const paramReadings = filtered.filter((r) => r.parameter === selectedParameter);
    if (paramReadings.length === 0) return null;
    return paramReadings[0]; // sorted newest first
  }, [filtered, selectedParameter]);

  // Match test kit for ideal range
  const matchingKit = useMemo(() => {
    if (!latestReading) return null;
    return ALL_TEST_KITS.find(
      (k) =>
        k.brand === latestReading.brand && k.parameter === latestReading.parameter,
    ) ?? null;
  }, [latestReading]);

  // Action plan severity
  const severity = useMemo<'good' | 'low' | 'high' | null>(() => {
    if (!latestReading || !matchingKit?.idealRange) return null;
    const { min, max } = matchingKit.idealRange;
    if (latestReading.value >= min && latestReading.value <= max) return 'good';
    return latestReading.value < min ? 'low' : 'high';
  }, [latestReading, matchingKit]);

  // ── Apex / Trident overlay window (oldest filtered reading → now) ──
  const [nowMs] = useState(() => Date.now());
  const overlayFromMs = useMemo(() => {
    let oldest = nowMs - 30 * 24 * 60 * 60 * 1_000;
    for (const r of filtered) {
      const t = Date.parse(r.timestamp);
      if (Number.isFinite(t) && t < oldest) oldest = t;
    }
    return oldest;
  }, [filtered, nowMs]);
  const overlay = useApexOverlay(
    selectedParameter !== 'All' ? selectedParameter : null,
    overlayFromMs,
    nowMs,
  );

  // ── Drift: latest manual reading vs nearest Apex point ─────────────
  const drift = useMemo(() => {
    if (!latestReading || !overlay.probeId || overlay.points.length === 0) return null;
    const t = Date.parse(latestReading.timestamp);
    if (!Number.isFinite(t)) return null;
    const d = computeDrift(latestReading.value, t, overlay.points, 12);
    if (!d) return null;
    return { ...d, tolerance: DRIFT_TOLERANCE[overlay.probeId] };
  }, [latestReading, overlay.probeId, overlay.points]);

  // ── Overdue tests (any logged parameter not tested in 7 days) ─────
  const overdue = useMemo(() => {
    const distinct = parameters.filter((p) => p !== 'All');
    return staleParameters(readings, distinct, nowMs, 7);
  }, [readings, parameters, nowMs]);

  // ── Per-parameter trend summaries (shown in "All" view) ───────────
  const trendSummaries = useMemo(() => {
    if (selectedParameter !== 'All') return [];
    return parameters
      .filter((p) => p !== 'All')
      .map((parameter) => {
        const mine = readings.filter((r) => r.parameter === parameter);
        if (mine.length < 2) return null;
        const latest = [...mine].sort((a, b) =>
          b.timestamp.localeCompare(a.timestamp),
        )[0]!;
        const trend = detectTrend(mine as ManualReading[], parameter);
        const periods = summarizePeriods(readings, parameter, nowMs);
        return { parameter, latest, trend, periods };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [selectedParameter, parameters, readings, nowMs]);

  const handleDelete = useCallback(async (id: string) => {
    await remove(id);
  }, []);

  const handleExportCsv = useCallback(() => {
    const csv = toCsv(filtered);
    downloadBlob(csv, 'parameter-logs.csv', 'text/csv;charset=utf-8;');
  }, [filtered]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-reef-100">Parameter Logs</h1>
        <p className="text-sm text-reef-400 mt-1">
          Track your manual water test results over time.
        </p>
      </header>

      {/* Filters bar */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        {/* Parameter chips */}
        <div className="flex flex-wrap gap-1.5">
          {parameters.map((p) => (
            <button
              key={p}
              onClick={() => setSelectedParameter(p)}
              className={`px-3 py-1 text-xs font-semibold border rounded-full transition-all duration-200 ${
                selectedParameter === p
                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
                  : 'bg-reef-800 text-reef-300 border-reef-700 hover:bg-reef-700'
              }`}
            >
              {p === 'All' ? 'All' : p}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-0" />

        {/* Brand filter */}
        <select
          value={brandFilter}
          onChange={(e) => setBrandFilter(e.target.value)}
          className="rounded-md border-reef-700 bg-reef-900 py-1.5 px-2 text-xs text-reef-200 shadow-sm focus:border-amber-500 focus:ring-amber-500"
          aria-label="Brand filter"
        >
          {brands.map((b) => (
            <option key={b} value={b}>{b === 'All' ? 'All brands' : b}</option>
          ))}
        </select>

        {/* Date range */}
        <div className="inline-flex bg-reef-800 border border-reef-700 rounded-lg p-0.5">
          {(['7d', '30d', '90d', 'all'] as const).map((r) => (
            <button
              key={r}
              onClick={() => setDateRange(r)}
              className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                dateRange === r
                  ? 'bg-amber-600 text-white'
                  : 'text-reef-300 hover:bg-reef-700'
              }`}
            >
              {r === 'all' ? 'All' : r}
            </button>
          ))}
        </div>

        {/* Export / Print / Report */}
        <button
          onClick={handleExportCsv}
          disabled={filtered.length === 0}
          className="px-3 py-1.5 text-xs font-semibold bg-reef-800 text-reef-200 border border-reef-700 rounded-md hover:bg-reef-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Export CSV
        </button>
        <button
          onClick={handlePrint}
          disabled={filtered.length === 0}
          className="px-3 py-1.5 text-xs font-semibold bg-reef-800 text-reef-200 border border-reef-700 rounded-md hover:bg-reef-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Print
        </button>
        <button
          onClick={() => setShowReport(true)}
          disabled={readings.length === 0}
          data-testid="open-health-report"
          className="px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Health Report
        </button>
      </div>

      {/* Overdue tests reminder */}
      {overdue.length > 0 && (
        <div
          className="mb-6 p-4 bg-sky-900/20 border border-sky-700/50 rounded-xl"
          data-testid="overdue-card"
        >
          <h3 className="text-sm font-semibold text-sky-200 mb-1">
            ⏰ Tests due
          </h3>
          <p className="text-xs text-reef-300">
            {overdue
              .map((s) =>
                s.daysSince === null
                  ? `${s.parameter} (never tested)`
                  : `${s.parameter} (${s.daysSince} days ago)`,
              )
              .join(' · ')}
          </p>
          <a
            href="/test-kits"
            className="inline-block mt-2 text-xs font-semibold text-sky-300 underline hover:text-sky-200"
          >
            Run a test →
          </a>
        </div>
      )}

      {/* Empty state */}
      {readings.length === 0 ? (
        <div className="text-center py-20 bg-reef-900 border border-reef-800 rounded-xl">
          <p className="text-lg font-semibold text-reef-300">
            No parameter logs yet.
          </p>
          <p className="text-sm text-reef-500 mt-2">
            Run a test from{' '}
            <a href="/test-kits" className="text-amber-400 underline">
              Test Kits
            </a>{' '}
            to start logging results.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-reef-900 border border-reef-800 rounded-xl">
          <p className="text-lg font-semibold text-reef-300">
            No readings match your filters.
          </p>
          <button
            onClick={() => {
              setSelectedParameter('All');
              setBrandFilter('All');
              setDateRange('all');
            }}
            className="mt-3 px-4 py-2 text-sm font-semibold text-reef-200 bg-reef-700 rounded-md hover:bg-reef-600 transition-colors"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          {/* Trend summary grid — "All" view */}
          {trendSummaries.length > 0 && (
            <div
              className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              data-testid="trend-summary-grid"
            >
              {trendSummaries.map(({ parameter, latest, trend, periods }) => (
                <button
                  key={parameter}
                  onClick={() => setSelectedParameter(parameter)}
                  className="text-left p-4 bg-reef-900 border border-reef-800 rounded-xl hover:border-amber-600/50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-reef-200">
                      {parameter}
                    </h4>
                    <span className="text-base">
                      {trend?.direction === 'rising'
                        ? '📈'
                        : trend?.direction === 'falling'
                          ? '📉'
                          : '➡️'}
                    </span>
                  </div>
                  <p className="mt-1 text-lg font-bold tabular-nums text-reef-100">
                    {latest.value}{' '}
                    <span className="text-xs font-normal text-reef-400">
                      {latest.unit}
                    </span>
                  </p>
                  <p className="mt-1 text-[11px] text-reef-500">
                    7d {periods.delta7d !== null
                      ? `${periods.delta7d >= 0 ? '+' : ''}${periods.delta7d.toFixed(2)}`
                      : '—'}{' '}
                    · 30d{' '}
                    {periods.delta30d !== null
                      ? `${periods.delta30d >= 0 ? '+' : ''}${periods.delta30d.toFixed(2)}`
                      : '—'}
                  </p>
                </button>
              ))}
            </div>
          )}

          {/* Trend card (Phase 5.4) */}
          {selectedParameter !== 'All' && (
            (() => {
              const trend = detectTrend(
                readings as ManualReading[],
                selectedParameter,
              );
              if (!trend) return null;
              const directionEmoji =
                trend.direction === 'rising'
                  ? '📈'
                  : trend.direction === 'falling'
                    ? '📉'
                    : '➡️';
              const confidenceLabel =
                trend.confidence === 'high'
                  ? 'High confidence'
                  : trend.confidence === 'medium'
                    ? 'Medium confidence'
                    : 'Low confidence';
              return (
                <div className="mb-6 p-4 bg-reef-900 border border-reef-800 rounded-xl">
                  <h3 className="text-sm font-semibold text-reef-200 mb-2">
                    {directionEmoji} {selectedParameter} Trend
                  </h3>
                  <p className="text-xs text-reef-400">
                    {trend.direction === 'rising'
                      ? 'Rising'
                      : trend.direction === 'falling'
                        ? 'Falling'
                        : 'Stable'}{' '}
                    at {Math.abs(trend.slopePerWeek).toFixed(2)} / week ·{' '}
                    {trend.weeksOfData} weeks of data · {confidenceLabel}
                  </p>
                </div>
              );
            })()
          )}

          {/* Drift card — Apex vs test kit disagreement */}
          {drift && latestReading && (
            <div
              data-testid="drift-card"
              className={`mb-6 p-4 rounded-xl border ${
                Math.abs(drift.diff) > drift.tolerance
                  ? 'bg-orange-900/20 border-orange-700/50'
                  : 'bg-reef-900 border-reef-800'
              }`}
            >
              <h3 className="text-sm font-semibold text-reef-200 mb-1">
                🔬 Apex cross-check
              </h3>
              <p className="text-xs text-reef-300">
                Your test: <strong>{latestReading.value}</strong> · Apex/Trident:{' '}
                <strong>{drift.apexValue.toFixed(2)}</strong> · Difference:{' '}
                <strong>
                  {drift.diff >= 0 ? '+' : ''}
                  {drift.diff.toFixed(2)}
                </strong>{' '}
                {latestReading.unit}
              </p>
              {Math.abs(drift.diff) > drift.tolerance ? (
                <p className="mt-1 text-xs text-orange-300">
                  That's outside the ±{drift.tolerance} {latestReading.unit} agreement
                  band — consider recalibrating your Apex probe or re-testing.
                </p>
              ) : (
                <p className="mt-1 text-xs text-emerald-300">
                  Within ±{drift.tolerance} {latestReading.unit} — your kit and the
                  Apex agree. ✔
                </p>
              )}
            </div>
          )}

          {/* Action Plan card */}
          {severity && latestReading && matchingKit && (
            <div
              className={`mb-6 p-4 rounded-xl border ${
                severity === 'good'
                  ? 'bg-green-900/20 border-green-700/50'
                  : severity === 'low'
                    ? 'bg-yellow-900/20 border-yellow-700/50'
                    : 'bg-red-900/20 border-red-700/50'
              }`}
            >
              <h3 className="text-sm font-semibold mb-2">
                {severity === 'good'
                  ? `✅ ${latestReading.parameter} looks good`
                  : `⚠️ ${latestReading.parameter} is ${
                      severity === 'low' ? 'below' : 'above'
                    } ideal range`}
              </h3>
              <p className="text-xs text-reef-300">
                Latest: {latestReading.value} {latestReading.unit} ({latestReading.brand})
              </p>
              {matchingKit.idealRange && (
                <p className="text-xs text-reef-400 mt-1">
                  Ideal: {matchingKit.idealRange.min}–{matchingKit.idealRange.max}{' '}
                  {matchingKit.resultUnit}
                </p>
              )}
              {severity !== 'good' && (
                <a
                  href={`/stock-solutions`}
                  className="inline-block mt-3 px-4 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
                >
                  Dose to fix this →
                </a>
              )}
            </div>
          )}

          {/* Trend chart with Apex/Trident overlay + ideal band */}
          {selectedParameter !== 'All' && (
            <ParameterTrendChart
              readings={filtered}
              apexPoints={overlay.points}
              idealRange={matchingKit?.idealRange ?? null}
              unit={latestReading?.unit ?? ''}
              parameter={selectedParameter}
            />
          )}

          {/* History — table on desktop, cards on phones */}
          <div className="bg-reef-900 border border-reef-800 rounded-xl overflow-hidden">
            {/* Mobile card list */}
            <ul className="md:hidden divide-y divide-reef-800" data-testid="readings-cards">
              {filtered.map((r) => (
                <li key={r.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-reef-100">
                        {r.parameter}:{' '}
                        <span className="font-mono">{r.value}</span>{' '}
                        <span className="text-reef-400 font-normal">{r.unit}</span>
                      </p>
                      <p className="mt-0.5 text-[11px] text-reef-500">
                        {formatDate(r.timestamp)} · {r.brand}
                      </p>
                      {r.notes ? (
                        <p className="mt-1 text-xs text-reef-400">{r.notes}</p>
                      ) : null}
                    </div>
                    <button
                      onClick={() => handleDelete(r.id)}
                      className="shrink-0 text-xs text-red-400 hover:text-red-300 transition-colors py-1"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-reef-800 text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-reef-400 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-reef-400 uppercase">
                      Value
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-reef-400 uppercase">
                      Unit
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-reef-400 uppercase">
                      Brand
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-reef-400 uppercase">
                      Notes
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-reef-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-reef-800 hover:bg-reef-800/50 transition-colors"
                    >
                      <td className="px-4 py-2 text-reef-300 whitespace-nowrap">
                        {formatDate(r.timestamp)}
                      </td>
                      <td className="px-4 py-2 text-reef-100 font-mono">
                        {r.value}
                      </td>
                      <td className="px-4 py-2 text-reef-400">{r.unit}</td>
                      <td className="px-4 py-2 text-reef-300">{r.brand}</td>
                      <td className="px-4 py-2 text-reef-400 max-w-48 truncate">
                        {r.notes ?? '—'}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="text-xs text-red-400 hover:text-red-300 transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-reef-800 text-xs text-reef-500">
              {filtered.length} reading{filtered.length !== 1 ? 's' : ''}
            </div>
          </div>
        </>
      )}

      {/* Health Report overlay */}
      {showReport && (
        <HealthReportView readings={readings} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}
